import * as Sentry from '@sentry/react';
import { Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

// Import directo, no por el barrel de `ui`: esta frontera vive en el chunk de
// entrada y el barrel arrastraría todo el design system con ella.
import { AppErrorFallback } from '@shared/components/ui/AppErrorFallback';
import { Spinner } from '@shared/components/ui/Spinner';
import { attemptChunkReload, isChunkLoadError } from '@shared/utils/chunkReload';

interface RouteBoundaryProps {
  children: ReactNode;
}

/**
 * Clave estable del límite de error para shells con pestañas por ruta hija.
 * Sin esto, `key={pathname}` remonta tabs+contenido al cambiar Activos↔Informes.
 * El detalle (`/merchandise-blocks/:id`) sigue usando el pathname completo.
 */
export function routeBoundaryResetKey(pathname: string): string {
  if (pathname === '/merchandise-blocks' || pathname === '/merchandise-blocks/history') {
    return 'layout:merchandise-blocks';
  }
  return pathname;
}

/**
 * Recuperación del chunk que ya no está en el servidor: recarga para traer el
 * índice desplegado y, mientras, mantiene el spinner de la navegación.
 *
 * Si la recarga no procede —ya se recargó por lo mismo, o no hay red— cae al
 * fallback de error, que ofrece la recarga manual. El intento va en un efecto y
 * no en el render para no colar el efecto secundario en una función pura.
 */
const ChunkReloadFallback = () => {
  const [reloadRejected, setReloadRejected] = useState(false);

  useEffect(() => {
    if (!attemptChunkReload()) {
      setReloadRejected(true);
    }
  }, []);

  if (reloadRejected) {
    return <AppErrorFallback variant="inline" />;
  }

  return <Spinner size="md" className="min-h-[24rem]" />;
};

/**
 * Frontera de carga y de error del área de contenido.
 *
 * Las rutas protegidas se cargan de forma diferida, así que entre la navegación
 * y el render de la pantalla hay una descarga de chunk. Esta frontera la absorbe
 * **dentro** del layout, de modo que la navegación y el header siguen montados;
 * si estuviera por encima (como el `<Suspense>` de `App.tsx`) cada navegación
 * dejaría la pantalla en blanco.
 *
 * El límite de error se monta siempre, con `VITE_SENTRY_DSN` o sin él: un chunk
 * puede fallar en cualquier entorno, típicamente tras un despliegue con la
 * pestaña abierta. Sin `Sentry.init` sigue funcionando como límite de error de
 * React, simplemente no reporta.
 *
 * Ese caso concreto no se presenta como error al usuario: si el fallo es de
 * descarga de chunk, `ChunkReloadFallback` recarga para traer la versión
 * desplegada. Se sigue reportando a Sentry, etiquetado, porque es la señal que
 * dice si el despliegue está dejando huérfanos los chunks anteriores.
 *
 * `key` reinicia el límite al navegar: sin él, el fallback de una ruta que
 * falló se queda pegado. En shells con tabs (p. ej. merchandise-blocks) la
 * clave es estable entre pestañas hermanas; el Shell local reinicia errores
 * del contenido con su propio límite keyed por pathname.
 */
export const RouteBoundary = ({ children }: RouteBoundaryProps) => {
  const { pathname } = useLocation();
  const boundaryKey = useMemo(() => routeBoundaryResetKey(pathname), [pathname]);

  return (
    <Sentry.ErrorBoundary
      key={boundaryKey}
      fallback={({ error }) =>
        isChunkLoadError(error) ? <ChunkReloadFallback /> : <AppErrorFallback variant="inline" />
      }
      beforeCapture={(scope, error) => {
        scope.setTag('error_boundary', 'route');
        scope.setTag('chunk_load_error', isChunkLoadError(error));
      }}
    >
      {/* Mismo tamaño que LoadingState de módulos (md): evita salto visual grande→chico al navegar */}
      <Suspense fallback={<Spinner size="md" className="min-h-[24rem]" />}>{children}</Suspense>
    </Sentry.ErrorBoundary>
  );
};
